import React, { useCallback, useState } from "react";
import {
  ArrowUp,
  ArrowDown,
  Copy,
  Trash2,
  Edit2,
  Plus,
  GitBranch,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ConditionalRule } from "../../types/conditionalRules";
import ConditionalRuleEditor from "./ConditionalRuleEditor";
import { useUIState } from "../prototype/UIStateProvider";
// import DateQuestionEditor from "./DateQuestionEditor"; // MIGRÉ vers apps/date-polls/src/components/

export type QuestionKind =
  | "single"
  | "multiple"
  | "text"
  | "long-text"
  | "matrix"
  | "rating"
  | "nps"
  | "date";

export type QuestionOption = {
  id: string;
  label: string;
  isOther?: boolean; // Option "Autre" avec champ texte libre
};

export type Question = {
  id: string;
  title: string;
  kind: QuestionKind;
  required?: boolean;
  options?: QuestionOption[]; // for single/multiple
  maxChoices?: number; // for multiple
  // Matrix-specific fields
  matrixRows?: QuestionOption[]; // Lignes (aspects à évaluer)
  matrixColumns?: QuestionOption[]; // Colonnes (échelle de réponse)
  matrixType?: "single" | "multiple"; // Une seule réponse par ligne ou plusieurs
  matrixColumnsNumeric?: boolean; // Colonnes numériques (1-5) au lieu de texte
  // Rating-specific fields
  ratingScale?: number; // 5 ou 10 (par défaut 5)
  ratingStyle?: "numbers" | "stars" | "emojis"; // Style d'affichage (par défaut numbers)
  ratingMinLabel?: string; // Label pour la valeur minimale
  ratingMaxLabel?: string; // Label pour la valeur maximale
  // Text validation fields
  validationType?: "email" | "phone" | "url" | "number" | "date"; // Type de validation pour champs text
  placeholder?: string; // Placeholder personnalisé
  allowMaybeVotes?: boolean; // Permettre les votes "peut-être"
  allowAnonymousVotes?: boolean; // Permettre les votes anonymes
  // Date-specific fields
  selectedDates?: string[];
  timeSlotsByDate?: Record<
    string,
    Array<{ hour: number; minute: number; enabled: boolean; duration?: number }>
  >;
  timeGranularity?: "15min" | "30min" | "1h";
};

export type QuestionCardProps = {
  question: Question;
  isActive: boolean;
  onEdit: () => void;
  onChange: (patch: Partial<Question>) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  // Conditional rules support
  allQuestions?: Question[];
  conditionalRules?: ConditionalRule[];
  onConditionalRulesChange?: (rules: ConditionalRule[]) => void;
};

export default function QuestionCard({
  question,
  isActive,
  onEdit,
  onChange,
  onDuplicate,
  onDelete,
  onMoveUp,
  onMoveDown,
  allQuestions,
  conditionalRules,
  onConditionalRulesChange,
}: QuestionCardProps) {
  const { t } = useTranslation("polls");
  const { highlightedId, highlightType } = useUIState();
  const [showConditionalEditor, setShowConditionalEditor] = useState(false);

  // Déterminer si cette question doit être animée
  const isHighlighted = highlightedId === question.id;
  const highlightClass =
    isHighlighted && highlightType ? `question-highlight-${highlightType}` : "";
  // Sauvegarder les options avant de changer de type (pour pouvoir les restaurer)
  const [savedOptions, setSavedOptions] = useState<
    QuestionOption[] | undefined
  >(question.options);
  const setTitle = useCallback(
    (title: string) => onChange({ title }),
    [onChange],
  );
  const setKind = useCallback(
    (kind: QuestionKind) => {
      // Sauvegarder les options actuelles si on quitte single/multiple
      if (
        (question.kind === "single" || question.kind === "multiple") &&
        question.options
      ) {
        setSavedOptions(question.options);
      }

      // reset incompatible fields when switching kind
      if (kind === "text" || kind === "long-text") {
        onChange({
          kind,
          options: undefined,
          maxChoices: undefined,
          matrixRows: undefined,
          matrixColumns: undefined,
          matrixType: undefined,
        });
      } else if (kind === "single") {
        onChange({
          kind,
          maxChoices: undefined,
          options: question.options ?? savedOptions ?? makeDefaultOptions(t),
          matrixRows: undefined,
          matrixColumns: undefined,
          matrixType: undefined,
        });
      } else if (kind === "multiple") {
        onChange({
          kind,
          options: question.options ?? savedOptions ?? makeDefaultOptions(t),
          maxChoices: question.maxChoices ?? 1,
          matrixRows: undefined,
          matrixColumns: undefined,
          matrixType: undefined,
        });
      } else if (kind === "matrix") {
        // Sauvegarder les options avant de passer en matrice
        if (question.options && question.options.length > 0) {
          setSavedOptions(question.options);
        }

        // Réutiliser les options existantes comme lignes de la matrice
        const defaultRows =
          question.options && question.options.length > 0
            ? question.options.map((opt) => ({ id: opt.id, label: opt.label }))
            : [
                {
                  id: rid(),
                  label: t("question_card.matrix.row_default", { index: 1 }),
                },
                {
                  id: rid(),
                  label: t("question_card.matrix.row_default", { index: 2 }),
                },
              ];

        onChange({
          kind,
          options: undefined,
          maxChoices: undefined,
          matrixRows: question.matrixRows ?? defaultRows,
          matrixColumns: question.matrixColumns ?? [
            { id: rid(), label: t("question_card.matrix.scale_not_at_all") },
            { id: rid(), label: t("question_card.matrix.scale_a_little") },
            { id: rid(), label: t("question_card.matrix.scale_moderately") },
            { id: rid(), label: t("question_card.matrix.scale_a_lot") },
            { id: rid(), label: t("question_card.matrix.scale_enormously") },
          ],
          matrixType: question.matrixType ?? "single",
        });
      } else if (kind === "rating") {
        onChange({
          kind,
          options: undefined,
          maxChoices: undefined,
          matrixRows: undefined,
          matrixColumns: undefined,
          matrixType: undefined,
          ratingScale: question.ratingScale ?? 5,
          ratingStyle: question.ratingStyle ?? "numbers",
          ratingMinLabel: question.ratingMinLabel ?? "",
          ratingMaxLabel: question.ratingMaxLabel ?? "",
        });
      } else if (kind === "nps") {
        onChange({
          kind,
          options: undefined,
          maxChoices: undefined,
          matrixRows: undefined,
          matrixColumns: undefined,
          matrixType: undefined,
          ratingScale: undefined,
          ratingStyle: undefined,
          ratingMinLabel: undefined,
          ratingMaxLabel: undefined,
        });
      } else if (kind === "date") {
        onChange({
          kind,
          options: undefined,
          maxChoices: undefined,
          matrixRows: undefined,
          matrixColumns: undefined,
          matrixType: undefined,
          ratingScale: undefined,
          ratingStyle: undefined,
          ratingMinLabel: undefined,
          ratingMaxLabel: undefined,
          selectedDates: question.selectedDates ?? [],
          timeSlotsByDate: question.timeSlotsByDate ?? {},
          timeGranularity: question.timeGranularity ?? "30min",
          allowMaybeVotes: question.allowMaybeVotes ?? false,
          allowAnonymousVotes: question.allowAnonymousVotes ?? false,
        });
      }
    },
    [
      onChange,
      question.kind,
      question.options,
      question.maxChoices,
      question.matrixRows,
      question.matrixColumns,
      question.matrixType,
      question.ratingScale,
      question.ratingStyle,
      question.ratingMinLabel,
      question.ratingMaxLabel,
      savedOptions,
      t,
    ],
  );
  const setRequired = useCallback(
    (required: boolean) => onChange({ required }),
    [onChange],
  );

  const addOption = useCallback(() => {
    const next = (question.options ?? []).concat({
      id: rid(),
      label: t("question_card.option_default"),
    });
    onChange({ options: next });
  }, [onChange, question.options, t]);

  const updateOption = useCallback(
    (optId: string, label: string) => {
      const next = (question.options ?? []).map((o) =>
        o.id === optId ? { ...o, label } : o,
      );
      onChange({ options: next });
    },
    [onChange, question.options],
  );

  const removeOption = useCallback(
    (optId: string) => {
      const next = (question.options ?? []).filter((o) => o.id !== optId);
      onChange({ options: next });
    },
    [onChange, question.options],
  );

  const setMaxChoices = useCallback(
    (n: number) => {
      if (!Number.isFinite(n) || n < 1) n = 1;
      onChange({ maxChoices: Math.floor(n) });
    },
    [onChange],
  );

  const toggleOtherOption = useCallback(() => {
    const hasOther = question.options?.some((o) => o.isOther);
    if (hasOther) {
      // Supprimer l'option "Autre"
      const next = (question.options ?? []).filter((o) => !o.isOther);
      onChange({ options: next });
    } else {
      // Ajouter l'option "Autre"
      const next = (question.options ?? []).concat({
        id: rid(),
        label: t("question_card.other_option_label"),
        isOther: true,
      });
      onChange({ options: next });
    }
  }, [onChange, question.options, t]);

  // Matrix-specific handlers
  const addMatrixRow = useCallback(() => {
    const next = (question.matrixRows ?? []).concat({
      id: rid(),
      label: t("question_card.matrix.row_generic"),
    });
    onChange({ matrixRows: next });
  }, [onChange, question.matrixRows, t]);

  const updateMatrixRow = useCallback(
    (rowId: string, label: string) => {
      const next = (question.matrixRows ?? []).map((r) =>
        r.id === rowId ? { ...r, label } : r,
      );
      onChange({ matrixRows: next });
    },
    [onChange, question.matrixRows],
  );

  const removeMatrixRow = useCallback(
    (rowId: string) => {
      const next = (question.matrixRows ?? []).filter((r) => r.id !== rowId);
      onChange({ matrixRows: next });
    },
    [onChange, question.matrixRows],
  );

  const addMatrixColumn = useCallback(() => {
    const next = (question.matrixColumns ?? []).concat({
      id: rid(),
      label: t("question_card.matrix.column_generic"),
    });
    onChange({ matrixColumns: next });
  }, [onChange, question.matrixColumns, t]);

  const updateMatrixColumn = useCallback(
    (colId: string, label: string) => {
      const next = (question.matrixColumns ?? []).map((c) =>
        c.id === colId ? { ...c, label } : c,
      );
      onChange({ matrixColumns: next });
    },
    [onChange, question.matrixColumns],
  );

  const removeMatrixColumn = useCallback(
    (colId: string) => {
      const next = (question.matrixColumns ?? []).filter((c) => c.id !== colId);
      onChange({ matrixColumns: next });
    },
    [onChange, question.matrixColumns],
  );

  const setMatrixType = useCallback(
    (type: "single" | "multiple") => {
      onChange({ matrixType: type });
    },
    [onChange],
  );

  const toggleMatrixColumnsNumeric = useCallback(() => {
    const isNumeric = !question.matrixColumnsNumeric;
    if (isNumeric) {
      // Passer en colonnes numériques (1-5)
      onChange({
        matrixColumnsNumeric: true,
        matrixColumns: [
          { id: rid(), label: "1" },
          { id: rid(), label: "2" },
          { id: rid(), label: "3" },
          { id: rid(), label: "4" },
          { id: rid(), label: "5" },
        ],
      });
    } else {
      // Revenir aux colonnes texte (échelle Likert)
      onChange({
        matrixColumnsNumeric: false,
        matrixColumns: [
          { id: rid(), label: t("question_card.matrix.scale_not_at_all") },
          { id: rid(), label: t("question_card.matrix.scale_a_little") },
          { id: rid(), label: t("question_card.matrix.scale_moderately") },
          { id: rid(), label: t("question_card.matrix.scale_a_lot") },
          { id: rid(), label: t("question_card.matrix.scale_enormously") },
        ],
      });
    }
  }, [onChange, question.matrixColumnsNumeric, t]);

  if (!isActive) {
    return (
      <div
        className={`rounded-md border border-border bg-card p-2 sm:p-4 flex flex-col gap-2 sm:gap-3 ${highlightClass}`}
      >
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div>
            <div className="font-medium break-words text-foreground">
              {question.title || t("question_card.untitled")}
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 justify-end">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground transition-colors"
              title={t("question_card.edit")}
              aria-label={t("question_card.edit")}
              data-testid="questioncard-button"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Voter-style preview for non-active cards */}
        {question.kind === "text" && (
          <input
            type="text"
            disabled
            placeholder={t("question_card.preview.your_answer")}
            className="w-full px-3 py-2 border border-border rounded bg-muted text-muted-foreground placeholder-muted-foreground"
          />
        )}
        {question.kind === "long-text" && (
          <textarea
            disabled
            placeholder={t("question_card.preview.your_detailed_answer")}
            rows={3}
            className="w-full px-3 py-2 border border-border rounded bg-muted text-muted-foreground placeholder-muted-foreground"
          />
        )}
        {(question.kind === "single" || question.kind === "multiple") && (
          <div className="space-y-2">
            {(question.options ?? []).map((opt) => (
              <label
                key={opt.id}
                className="flex items-center gap-2 text-sm text-muted-foreground"
              >
                {question.kind === "single" ? (
                  <input type="radio" disabled />
                ) : (
                  <input type="checkbox" disabled />
                )}
                <span>{opt.label || t("question_card.option_default")}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-md border border-border bg-card p-2 sm:p-4 flex flex-col gap-2 sm:gap-3 ${highlightClass}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <label htmlFor={`kind-select-${question.id}`} className="sr-only">
            {t("question_card.question_type")}
          </label>
          <select
            id={`kind-select-${question.id}`}
            aria-label={t("question_card.question_type")}
            className="rounded-md border border-border bg-muted text-foreground px-2 py-1.5 sm:px-3 sm:py-2 text-sm sm:text-base"
            value={question.kind}
            onChange={(e) => setKind(e.target.value as QuestionKind)}
            data-testid="question-kind-select"
            data-qid={question.id}
          >
            <option value="single">{t("question_card.kind.single")}</option>
            <option value="multiple">{t("question_card.kind.multiple")}</option>
            <option value="text">{t("question_card.kind.text")}</option>
            <option value="long-text">
              {t("question_card.kind.long_text")}
            </option>
            <option value="matrix">{t("question_card.kind.matrix")}</option>
            <option value="rating">{t("question_card.kind.rating")}</option>
            <option value="nps">{t("question_card.kind.nps")}</option>
            <option value="date">{t("question_card.kind.date")}</option>
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
          <button
            type="button"
            onClick={onMoveUp}
            className="inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-md border border-border text-muted-foreground hover:bg-accent transition-colors flex-shrink-0"
            title={t("question_card.move_up")}
            aria-label={t("question_card.move_up")}
            data-testid="question-move-up"
            data-qid={question.id}
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            className="inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-md border border-border text-muted-foreground hover:bg-accent transition-colors flex-shrink-0"
            title={t("question_card.move_down")}
            aria-label={t("question_card.move_down")}
            data-testid="question-move-down"
            data-qid={question.id}
          >
            <ArrowDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDuplicate}
            className="inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-md border border-border text-muted-foreground hover:bg-accent transition-colors flex-shrink-0"
            title={t("question_card.duplicate")}
            aria-label={t("question_card.duplicate")}
            data-testid="question-duplicate"
            data-qid={question.id}
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-md border border-border text-muted-foreground hover:bg-destructive hover:border-destructive transition-colors flex-shrink-0"
            title={t("question_card.delete")}
            aria-label={t("question_card.delete")}
            data-testid="question-delete"
            data-qid={question.id}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          {allQuestions && onConditionalRulesChange && (
            <button
              type="button"
              onClick={() => setShowConditionalEditor(!showConditionalEditor)}
              className={`inline-flex items-center gap-1 h-8 sm:h-9 px-2 sm:px-3 rounded-md border text-xs sm:text-sm transition-colors ${
                showConditionalEditor
                  ? "bg-primary border-primary text-primary-foreground"
                  : "border-border text-muted-foreground hover:bg-accent"
              }`}
              title={t("question_card.conditional_rules")}
              aria-label={t("question_card.conditional_rules")}
              data-testid="question-conditional"
              data-qid={question.id}
            >
              <GitBranch className="w-4 h-4" />
              <span className="hidden sm:inline">
                {t("question_card.conditions")}
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label
          htmlFor={`title-input-${question.id}`}
          className="text-sm font-medium text-muted-foreground"
        >
          {t("question_card.question_label")}
        </label>
        <input
          id={`title-input-${question.id}`}
          className="w-full rounded-md border border-border bg-muted text-foreground placeholder-muted-foreground px-2 py-1.5 sm:px-3 sm:py-2 text-sm sm:text-base focus:ring-2 focus:ring-ring outline-none"
          placeholder={t("question_card.question_placeholder")}
          value={question.title}
          onChange={(e) => setTitle(e.target.value)}
          data-testid="question-title-input"
          data-qid={question.id}
        />
      </div>

      {(question.kind === "single" || question.kind === "multiple") && (
        <div className="flex flex-col gap-2">
          <div className="text-sm text-muted-foreground">
            {t("question_card.options")}
          </div>
          <div className="flex flex-col gap-2">
            {(question.options ?? []).map((opt) => (
              <div key={opt.id} className="flex items-center gap-2">
                <input
                  className={`flex-1 rounded-md border px-2 py-1 text-sm sm:text-base ${
                    opt.isOther
                      ? "bg-primary/20 border-primary text-foreground"
                      : "border-border bg-muted text-foreground placeholder-muted-foreground"
                  }`}
                  value={opt.label || ""}
                  onChange={(e) => updateOption(opt.id, e.target.value)}
                  readOnly={opt.isOther}
                  disabled={opt.isOther}
                  placeholder={
                    opt.isOther
                      ? t("question_card.other_option_placeholder")
                      : t("question_card.enter_option")
                  }
                  data-testid="question-option-input"
                  data-qid={question.id}
                  data-optid={opt.id}
                />
                {opt.isOther && (
                  <span className="text-xs text-primary whitespace-nowrap">
                    {t("question_card.free_text")}
                  </span>
                )}
                {!opt.isOther && (
                  <button
                    type="button"
                    onClick={() => removeOption(opt.id)}
                    className="inline-flex items-center justify-center h-8 w-8 sm:h-9 sm:w-9 rounded-md border border-border text-muted-foreground hover:bg-destructive hover:border-destructive transition-colors"
                    title={t("question_card.remove_option")}
                    aria-label={t("question_card.remove_option")}
                    data-testid="question-option-remove"
                    data-qid={question.id}
                    data-optid={opt.id}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {question.kind === "multiple" && (
            <div className="flex items-center gap-3 bg-muted p-2 rounded-lg border border-border/50 w-fit">
              <label
                htmlFor={`max-choices-${question.id}`}
                className="text-sm font-medium text-muted-foreground"
              >
                {t("question_card.max_choices")}
              </label>
              <input
                id={`max-choices-${question.id}`}
                type="number"
                min={1}
                className="w-16 sm:w-20 rounded-md border border-border bg-muted text-foreground px-2 py-1 text-sm sm:text-base focus:ring-2 focus:ring-ring outline-none"
                value={question.maxChoices ?? 1}
                onChange={(e) => setMaxChoices(parseInt(e.target.value, 10))}
                data-testid="question-maxchoices"
                data-qid={question.id}
              />
            </div>
          )}
        </div>
      )}

      {/* Matrix editor */}
      {question.kind === "matrix" && (
        <div className="flex flex-col gap-3">
          {/* Matrix type selector */}
          <div className="flex items-center gap-3">
            <label
              htmlFor={`matrix-type-${question.id}`}
              className="text-sm font-medium text-muted-foreground"
            >
              {t("question_card.matrix.response_type")}
            </label>
            <select
              id={`matrix-type-${question.id}`}
              aria-label={t("question_card.matrix.response_type_aria")}
              className="rounded-md border border-border bg-muted text-foreground px-2 py-1.5 text-sm focus:ring-2 focus:ring-ring outline-none"
              value={question.matrixType ?? "single"}
              onChange={(e) =>
                setMatrixType(e.target.value as "single" | "multiple")
              }
            >
              <option value="single">
                {t("question_card.matrix.one_per_row")}
              </option>
              <option value="multiple">
                {t("question_card.matrix.several_per_row")}
              </option>
            </select>
          </div>

          {/* Rows editor */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {t("question_card.matrix.rows_heading")}
              </div>
              <button
                type="button"
                onClick={addMatrixRow}
                className="inline-flex items-center rounded-md border border-border text-muted-foreground hover:bg-accent transition-colors h-7 px-2 text-xs"
                title={t("question_card.matrix.add_row")}
                data-testid="questioncard-button"
              >
                <Plus className="w-3 h-3 mr-1" />
                {t("question_card.matrix.row")}
              </button>
            </div>
            <div className="flex flex-col gap-1">
              {(question.matrixRows ?? []).map((row) => (
                <div key={row.id} className="flex items-center gap-2">
                  <input
                    className="flex-1 rounded-md border border-border bg-muted text-foreground placeholder-muted-foreground px-2 py-1 text-sm"
                    value={row.label}
                    onChange={(e) => updateMatrixRow(row.id, e.target.value)}
                    placeholder={t("question_card.matrix.row_name")}
                  />
                  <button
                    type="button"
                    onClick={() => removeMatrixRow(row.id)}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border text-muted-foreground hover:bg-destructive hover:border-destructive transition-colors"
                    title={t("question_card.matrix.remove_row")}
                    data-testid="questioncard-button"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Columns editor */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {t("question_card.matrix.columns_heading")}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={toggleMatrixColumnsNumeric}
                  className={`inline-flex items-center rounded-md border h-7 px-2 text-xs transition-colors ${
                    question.matrixColumnsNumeric
                      ? "bg-primary border-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:bg-accent"
                  }`}
                  title={
                    question.matrixColumnsNumeric
                      ? t("question_card.matrix.switch_to_text")
                      : t("question_card.matrix.switch_to_numeric")
                  }
                  data-testid="questioncard-button"
                >
                  {question.matrixColumnsNumeric
                    ? t("question_card.matrix.toggle_text")
                    : t("question_card.matrix.toggle_numeric")}
                </button>
                <button
                  type="button"
                  onClick={addMatrixColumn}
                  className="inline-flex items-center rounded-md border border-border text-muted-foreground hover:bg-accent transition-colors h-7 px-2 text-xs"
                  title={t("question_card.matrix.add_column")}
                  data-testid="questioncard-button"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  {t("question_card.matrix.column")}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              {(question.matrixColumns ?? []).map((col) => (
                <div key={col.id} className="flex items-center gap-2">
                  <input
                    className="flex-1 rounded-md border border-border bg-muted text-foreground placeholder-muted-foreground px-2 py-1 text-sm"
                    value={col.label}
                    onChange={(e) => updateMatrixColumn(col.id, e.target.value)}
                    placeholder={t("question_card.matrix.column_name")}
                  />
                  <button
                    type="button"
                    onClick={() => removeMatrixColumn(col.id)}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-border text-muted-foreground hover:bg-destructive hover:border-destructive transition-colors"
                    title={t("question_card.matrix.remove_column")}
                    data-testid="questioncard-button"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rating editor */}
      {question.kind === "rating" && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            {/* Scale selector */}
            <div className="grid grid-cols-2 gap-4">
              {/* Scale selector */}
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor={`rating-scale-${question.id}`}
                  className="text-sm font-medium text-muted-foreground"
                >
                  {t("question_card.rating.scale")}
                </label>
                <select
                  id={`rating-scale-${question.id}`}
                  aria-label={t("question_card.rating.scale_aria")}
                  className="rounded-md border border-border bg-muted text-foreground px-3 py-2 text-sm focus:ring-2 focus:ring-ring outline-none"
                  value={question.ratingScale ?? 5}
                  onChange={(e) =>
                    onChange({ ratingScale: parseInt(e.target.value, 10) })
                  }
                >
                  <option value={5}>{t("question_card.rating.range_5")}</option>
                  <option value={10}>
                    {t("question_card.rating.range_10")}
                  </option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor={`rating-style-${question.id}`}
                  className="text-sm font-medium text-muted-foreground"
                >
                  {t("question_card.rating.style")}
                </label>
                <select
                  id={`rating-style-${question.id}`}
                  aria-label={t("question_card.rating.style_aria")}
                  className="rounded-md border border-border bg-muted text-foreground px-3 py-2 text-sm focus:ring-2 focus:ring-ring outline-none"
                  value={question.ratingStyle ?? "numbers"}
                  onChange={(e) =>
                    onChange({
                      ratingStyle: e.target.value as
                        | "numbers"
                        | "stars"
                        | "emojis",
                    })
                  }
                >
                  <option value="numbers">
                    {t("question_card.rating.numbers")}
                  </option>
                  <option value="stars">
                    {t("question_card.rating.stars")}
                  </option>
                  <option value="emojis">
                    {t("question_card.rating.emojis")}
                  </option>
                </select>
              </div>
            </div>

            {/* Labels min/max */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-muted-foreground">
                  {t("question_card.rating.min_label")}
                </label>
                <input
                  type="text"
                  className="rounded-md border border-border bg-muted text-foreground placeholder-muted-foreground px-2 py-1 text-sm"
                  placeholder={t("question_card.rating.min_placeholder")}
                  value={question.ratingMinLabel ?? ""}
                  onChange={(e) => onChange({ ratingMinLabel: e.target.value })}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-muted-foreground">
                  {t("question_card.rating.max_label")}
                </label>
                <input
                  type="text"
                  className="rounded-md border border-border bg-muted text-foreground placeholder-muted-foreground px-2 py-1 text-sm"
                  placeholder={t("question_card.rating.max_placeholder")}
                  value={question.ratingMaxLabel ?? ""}
                  onChange={(e) => onChange({ ratingMaxLabel: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NPS editor (pas de configuration, juste un message informatif) */}
      {question.kind === "nps" && (
        <div className="p-3 bg-primary/20 border border-primary rounded-lg">
          <p className="text-sm text-primary">
            <strong>{t("question_card.nps.title")}</strong>{" "}
            {t("question_card.nps.description")}
          </p>
          <p className="text-xs text-primary mt-1">
            {t("question_card.nps.legend")}
          </p>
        </div>
      )}

      {/* Text editor - Validation type */}
      {(question.kind === "text" || question.kind === "long-text") && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor={`validation-type-${question.id}`}
              className="text-sm font-medium text-muted-foreground"
            >
              {t("question_card.validation.label")}
            </label>
            <select
              id={`validation-type-${question.id}`}
              aria-label={t("question_card.validation.aria")}
              className="rounded-md border border-border bg-muted text-foreground px-3 py-2 text-sm focus:ring-2 focus:ring-ring outline-none"
              value={question.validationType ?? ""}
              onChange={(e) =>
                onChange({
                  validationType:
                    (e.target.value as Question["validationType"]) || undefined,
                })
              }
            >
              <option value="">{t("question_card.validation.none")}</option>
              <option value="email">
                {t("question_card.validation.email")}
              </option>
              <option value="phone">
                {t("question_card.validation.phone")}
              </option>
              <option value="url">{t("question_card.validation.url")}</option>
              <option value="number">
                {t("question_card.validation.number")}
              </option>
              <option value="date">{t("question_card.validation.date")}</option>
            </select>
          </div>
          {question.validationType && (
            <div className="p-2 bg-success/20 border border-success rounded text-xs text-success">
              {t("question_card.validation.auto_hint", {
                format: question.validationType,
              })}
            </div>
          )}
          <div className="flex flex-col gap-1">
            <label className="text-sm text-muted-foreground">
              {t("question_card.placeholder_label")}
            </label>
            <input
              type="text"
              className="rounded-md border border-border bg-muted text-foreground placeholder-muted-foreground px-2 py-1 text-sm"
              placeholder={t("question_card.placeholder_example")}
              value={question.placeholder ?? ""}
              onChange={(e) => onChange({ placeholder: e.target.value })}
            />
          </div>
        </div>
      )}

      {/* Footer row: Add option (if applicable) + Required toggle side-by-side */}
      {/* Actions bar - Only show for relevant question types */}
      <div className="pt-1 flex items-center gap-3 flex-wrap">
        {(question.kind === "single" || question.kind === "multiple") && (
          <>
            <button
              type="button"
              onClick={addOption}
              className="inline-flex items-center rounded-md border border-border text-muted-foreground hover:bg-accent transition-colors h-8 sm:h-9 px-2 sm:px-3 text-sm"
              title={t("question_card.add_option")}
              aria-label={t("question_card.add_option")}
              data-testid="question-add-option"
              data-qid={question.id}
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline ml-2">
                {t("question_card.add_option")}
              </span>
            </button>
            <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
              <input
                type="checkbox"
                checked={question.options?.some((o) => o.isOther) ?? false}
                onChange={toggleOtherOption}
                data-testid="question-other-option"
                data-qid={question.id}
              />
              {t("question_card.other_option_toggle")}
            </label>
          </>
        )}
        {/* Checkbox "Obligatoire" visible pour tous les types */}
        <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
          <input
            type="checkbox"
            checked={!!question.required}
            onChange={(e) => setRequired(e.target.checked)}
            data-testid="question-required"
            data-qid={question.id}
          />
          {t("question_card.required")}
        </label>
      </div>

      {/* Conditional Rules Editor */}
      {showConditionalEditor &&
        allQuestions &&
        conditionalRules !== undefined &&
        onConditionalRulesChange && (
          <div className="mt-3">
            <ConditionalRuleEditor
              questionId={question.id}
              questions={allQuestions}
              existingRules={conditionalRules}
              onChange={onConditionalRulesChange}
            />
          </div>
        )}

      {/* actions déplacées dans la barre d'entête pour gagner de l'espace vertical */}
    </div>
  );
}

function rid() {
  return Math.random().toString(36).slice(2, 8);
}

function makeDefaultOptions(
  t: (key: string, options?: Record<string, unknown>) => string,
): QuestionOption[] {
  return [
    {
      id: rid(),
      label: t("question_card.option_default_numbered", { index: 1 }),
    },
    {
      id: rid(),
      label: t("question_card.option_default_numbered", { index: 2 }),
    },
  ];
}

function labelForKind(kind: QuestionKind, t: (key: string) => string) {
  switch (kind) {
    case "single":
      return t("question_card.kind.single");
    case "multiple":
      return t("question_card.kind.multiple");
    case "text":
      return t("question_card.kind.text");
    case "long-text":
      return t("question_card.kind.long_text");
    default:
      return kind;
  }
}
